/**
 * ArticleContext — provides the ArticleDocument, run metadata, and shared
 * actions to all stage components and the document editor.
 *
 * The context OWNS the run state: it initializes from the prop but manages
 * it internally so that refreshRun() can update stages[] mid-pipeline without
 * waiting for the parent article list to reload.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ArticleDocument } from './types';
import type { BlogCandidate } from '../../services/blogCandidatesService';
import type { BlogRun } from '../../services/blogRunsService';
import { blogRunsService } from '../../services/blogRunsService';
import { jobsService } from '../../services/jobsService';
import type { JobRecord } from '../../services/jobsService';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

export interface ActiveJob {
  jobId: string;
  status: string;
  progress: any[];
}

export interface ArticleContextValue {
  document: ArticleDocument | null;
  candidate: BlogCandidate | null;
  /** The current blog run — owned by this context, updated via refreshRun(). */
  run: BlogRun | null;
  loading: boolean;
  error: string | null;
  /** Re-fetch the document from S3 (does NOT update run state). */
  refreshDocument: () => Promise<void>;
  /** Re-fetch the run record from DynamoDB. Updates stages[], status, etc. */
  refreshRun: () => Promise<void>;
  executeStage: (
    stageId: string,
    options?: {
      payload?: Record<string, any>;
      onUpdate?: (job: JobRecord) => void;
    }
  ) => Promise<JobRecord>;
  currentFrontier: string | null;
  refreshArticles: () => Promise<void>;
  editorOpen: boolean;
  editorFocusSection: string | null;
  openEditor: (focusSection?: string) => void;
  closeEditor: () => void;
  /** Active job detected on load or started by this session. */
  activeJob: ActiveJob | null;
  /** True when any job is in flight for this article. */
  isAnyJobActive: boolean;
}

const ArticleContext = createContext<ArticleContextValue | null>(null);

export function useArticleContext(): ArticleContextValue {
  const ctx = useContext(ArticleContext);
  if (!ctx) {
    throw new Error('useArticleContext must be used within an ArticleContextProvider');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ArticleContextProviderProps {
  candidate: BlogCandidate | null;
  run: BlogRun | null;
  onArticlesRefresh?: () => Promise<void>;
  children: React.ReactNode;
}

export function ArticleContextProvider({
  candidate,
  run: initialRun,
  onArticlesRefresh,
  children,
}: ArticleContextProviderProps) {
  // --- Run state: owned here, initialized from prop ---
  // The context is the single owner of run state. It initializes from the
  // parent prop but thereafter only updates via refreshRun(). The parent
  // prop is only used when switching to a different article (run_id changes).
  const [run, setRun] = useState<BlogRun | null>(initialRun);
  const [document, setDocument] = useState<ArticleDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorFocusSection, setEditorFocusSection] = useState<string | null>(null);

  const lastLoadedRunId = useRef<string | null>(null);

  // Reset run state when a DIFFERENT article is selected (run_id changes).
  // After this initial set, refreshRun() is the only way run state changes.
  const currentRunId = useRef(initialRun?.run_id);
  useEffect(() => {
    if (initialRun?.run_id !== currentRunId.current) {
      currentRunId.current = initialRun?.run_id;
      setRun(initialRun);
    }
  }, [initialRun?.run_id]);

  // On mount and when the run_id changes, fetch the latest run from the backend
  // to ensure we have the freshest data (not a stale parent prop).
  useEffect(() => {
    if (initialRun?.run_id) {
      blogRunsService.get(initialRun.run_id).then(setRun).catch(() => {});
    }
  }, [initialRun?.run_id]);

  // ---- Refresh run from backend ----
  const refreshRun = useCallback(async () => {
    const runId = run?.run_id;
    if (!runId) return;
    try {
      const freshRun = await blogRunsService.get(runId);
      setRun(freshRun);
    } catch (e: any) {
      console.error('Failed to refresh run:', e);
    }
  }, [run?.run_id]);

  // ---- Compute the current frontier (latest succeeded stage) ----
  const currentFrontier = useMemo<string | null>(() => {
    if (!run) return null;
    const stages = run.stages || [];
    for (let i = stages.length - 1; i >= 0; i--) {
      if (stages[i].status === 'succeeded') {
        return stages[i].stage_id;
      }
    }
    return run.current_stage_id || null;
  }, [run]);

  // ---- Load document from API ----
  const refreshDocument = useCallback(async () => {
    const runId = run?.run_id;
    if (!runId) {
      setDocument(null);
      setError(null);
      lastLoadedRunId.current = null;
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const doc = await blogRunsService.getDocument(runId) as ArticleDocument;
      setDocument(doc);
      lastLoadedRunId.current = runId;
    } catch (e: any) {
      setError(e?.message || 'Failed to load document');
      setDocument(null);
    } finally {
      setLoading(false);
    }
  }, [run?.run_id]);

  // Auto-load document when run changes
  useEffect(() => {
    if (run?.run_id) {
      void refreshDocument();
    }
  }, [run?.run_id, run?.updatedAt, refreshDocument]);

  // Clear document when candidate changes (no run selected)
  useEffect(() => {
    if (!run) {
      setDocument(null);
      setError(null);
      lastLoadedRunId.current = null;
    }
  }, [run]);

  // ---- Active job detection + polling ----
  const [activeJob, setActiveJob] = useState<ActiveJob | null>(null);

  useEffect(() => {
    const jobId = run?.pipeline_job_id;
    if (!jobId) {
      setActiveJob(null);
      return;
    }

    // Check if the job is already terminal — don't poll, don't loop
    let cancelled = false;
    const poll = async () => {
      try {
        const job = await jobsService.get(jobId);
        if (cancelled) return;
        if (job.status === 'succeeded' || job.status === 'failed' || job.status === 'cancelled') {
          // Job is done — clear active state and refresh data once
          setActiveJob(null);
          // Use a ref to avoid re-triggering this effect
          await refreshDocument();
          if (onArticlesRefresh) await onArticlesRefresh();
          return;
        }
        setActiveJob({ jobId: job.job_id, status: job.status, progress: job.progress || [] });
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setActiveJob(null);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [run?.pipeline_job_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAnyJobActive = !!activeJob;

  // ---- Execute a stage ----
  const executeStage = useCallback(
    async (
      stageId: string,
      options?: {
        payload?: Record<string, any>;
        onUpdate?: (job: JobRecord) => void;
      }
    ): Promise<JobRecord> => {
      const runId = run?.run_id;
      if (!runId) {
        throw new Error('No run selected');
      }
      const { job_id } = await jobsService.submit('blog_stage_execute', {
        run_id: runId,
        stage_id: stageId,
        ...(options?.payload || {}),
      });
      const finalJob = await jobsService.waitForCompletion(job_id, {
        onUpdate: options?.onUpdate,
      });
      // Refresh both run (for stage statuses) and document (for content)
      await refreshRun();
      await refreshDocument();
      // Throw if the job failed so callers (StagePanel) can show the error
      if (finalJob.status === 'failed') {
        throw new Error((finalJob as any).error || 'Stage execution failed');
      }
      if (onArticlesRefresh) {
        await onArticlesRefresh();
      }
      return finalJob;
    },
    [run?.run_id, refreshRun, refreshDocument, onArticlesRefresh]
  );

  // ---- Editor navigation ----
  const openEditor = useCallback((focusSection?: string) => {
    setEditorOpen(true);
    setEditorFocusSection(focusSection || null);
  }, []);

  const closeEditor = useCallback(() => {
    setEditorOpen(false);
    setEditorFocusSection(null);
  }, []);

  const refreshArticles = useCallback(async () => {
    if (onArticlesRefresh) {
      await onArticlesRefresh();
    }
  }, [onArticlesRefresh]);

  const value = useMemo<ArticleContextValue>(
    () => ({
      document,
      candidate,
      run,
      loading,
      error,
      refreshDocument,
      refreshRun,
      executeStage,
      currentFrontier,
      refreshArticles,
      editorOpen,
      editorFocusSection,
      openEditor,
      closeEditor,
      activeJob,
      isAnyJobActive,
    }),
    [document, candidate, run, loading, error, refreshDocument, refreshRun, executeStage, currentFrontier, refreshArticles, editorOpen, editorFocusSection, openEditor, closeEditor, activeJob, isAnyJobActive]
  );

  return (
    <ArticleContext.Provider value={value}>
      {children}
    </ArticleContext.Provider>
  );
}
